import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { useTranslation } from 'react-i18next';
import { FaPlus, FaMinus, FaShoppingCart } from 'react-icons/fa';
import '../styles/ProductCard.css';

// Вспомогательная функция для округления до 1 знака после запятой
const roundToOne = (num) => Math.round(num * 10) / 10;

const ProductCard = ({ product }) => {
  const { addToCart, cart, updateQuantity } = useCart();
  const { t } = useTranslation();
  
  // Кратность для товаров с кг (минимальный шаг добавления)
  const multiplicity = product.unit === 'kg' ? (product.multiplicity || 1) : 1;
  
  const [quantityPiece, setQuantityPiece] = useState(multiplicity);
  const [quantityBox, setQuantityBox] = useState(1);

  const cartItemPiece = cart.find(item => item.product._id === product._id && item.unit === 'piece');
  const cartItemBox = cart.find(item => item.product._id === product._id && item.unit === 'box');
  const cartQuantityPiece = cartItemPiece ? cartItemPiece.quantity : 0;
  const cartQuantityBox = cartItemBox ? cartItemBox.quantity : 0;

  const price = product.finalPrice || product.price;
  const boxPrice = product.finalBoxPrice || product.boxPrice;
  
  // Получаем информацию о скидках
  const personalDiscount = product.personalDiscount || { type: null, value: 0, amount: 0 };
  const generalDiscountType = product.generalDiscountType || 'none';
  const generalDiscountValue = product.generalDiscountValue || 0;
  
  // Определяем, есть ли скидка
  const hasDiscount = (personalDiscount.value > 0 || (generalDiscountType !== 'none' && generalDiscountValue > 0)) && price < product.price;
  
  // Определяем, какая скидка фактически применена (какая дала лучшую цену)
  let discountDisplay = '';
  let appliedDiscountType = 'none'; // 'personal' или 'general'
  
  if (personalDiscount.value > 0 || (generalDiscountType !== 'none' && generalDiscountValue > 0)) {
    // Рассчитываем цены с каждой скидкой
    let priceWithPersonal = product.price;
    if (personalDiscount.type === 'percent') {
      priceWithPersonal = product.price * (1 - personalDiscount.value / 100);
    } else if (personalDiscount.type === 'fixed') {
      priceWithPersonal = product.price - personalDiscount.value;
    } else if (personalDiscount.type === 'fixed_price') {
      priceWithPersonal = personalDiscount.value;
    }
    
    let priceWithGeneral = product.price;
    if (generalDiscountType === 'percent' && generalDiscountValue > 0) {
      priceWithGeneral = product.price * (1 - generalDiscountValue / 100);
    } else if (generalDiscountType === 'fixed' && generalDiscountValue > 0) {
      priceWithGeneral = product.price - generalDiscountValue;
    } else if (generalDiscountType === 'fixed_price' && generalDiscountValue > 0) {
      priceWithGeneral = generalDiscountValue;
    }
    
    // Определяем, какая скидка применилась
    if (priceWithPersonal <= priceWithGeneral && personalDiscount.value > 0) {
      appliedDiscountType = 'personal';
      if (personalDiscount.type === 'percent') {
        discountDisplay = `-${personalDiscount.value}%`;
      } else if (personalDiscount.type === 'fixed') {
        discountDisplay = `-${personalDiscount.value.toFixed(2)}€`;
      } else if (personalDiscount.type === 'fixed_price') {
        const discountAmount = product.price - personalDiscount.value;
        const discountPercent = ((discountAmount / product.price) * 100).toFixed(0);
        discountDisplay = `-${discountPercent}%`;
      }
    } else if (generalDiscountType !== 'none' && generalDiscountValue > 0) {
      appliedDiscountType = 'general';
      if (generalDiscountType === 'percent') {
        discountDisplay = `-${generalDiscountValue}%`;
      } else if (generalDiscountType === 'fixed') {
        discountDisplay = `-${generalDiscountValue.toFixed(2)}€`;
      } else if (generalDiscountType === 'fixed_price') {
        const discountAmount = product.price - generalDiscountValue;
        const discountPercent = ((discountAmount / product.price) * 100).toFixed(0);
        discountDisplay = `-${discountPercent}%`;
      }
    }
  }

  const handleAddToCart = (e, unit = 'piece') => {
    e.preventDefault();
    e.stopPropagation();
    const qty = unit === 'box' ? quantityBox : quantityPiece;
    addToCart(product, qty, unit);
    
    if (unit === 'box') {
      setQuantityBox(1);
    } else {
      setQuantityPiece(multiplicity);
    }
  };

  const handleIncrement = (e, unit = 'piece') => {
    e.preventDefault();
    e.stopPropagation();
    if (unit === 'box') {
      if (cartQuantityBox > 0) {
        updateQuantity(product._id, cartQuantityBox + 1, 'box');
      } else {
        setQuantityBox(prev => prev + 1);
      }
    } else {
      // Для кг товаров увеличиваем на кратность с округлением
      const step = multiplicity;
      if (cartQuantityPiece > 0) {
        updateQuantity(product._id, roundToOne(cartQuantityPiece + step), 'piece');
      } else {
        setQuantityPiece(prev => roundToOne(prev + step));
      }
    }
  };

  const handleDecrement = (e, unit = 'piece') => {
    e.preventDefault();
    e.stopPropagation();
    if (unit === 'box') {
      if (cartQuantityBox > 0) {
        updateQuantity(product._id, cartQuantityBox - 1, 'box');
      } else {
        setQuantityBox(prev => Math.max(1, prev - 1));
      }
    } else {
      // Для кг товаров уменьшаем на кратность с округлением
      const step = multiplicity;
      if (cartQuantityPiece > 0) {
        updateQuantity(product._id, Math.max(0, roundToOne(cartQuantityPiece - step)), 'piece');
      } else {
        setQuantityPiece(prev => Math.max(multiplicity, roundToOne(prev - step)));
      }
    }
  };

  const getUnitLabel = (unit) => {
    switch (unit) {
      case 'kg': return t('kg');
      case 'piece': return t('piece');
      case 'box': return t('boxUnit');
      default: return unit;
    }
  };
  
  return (
    <Link to={`/product/${product._id}`} className="product-card">
      {hasDiscount && (
        <div className="discount-badge" title={
          personalDiscount.value > 0 && generalDiscountType !== 'none' && generalDiscountValue > 0
            ? (appliedDiscountType === 'personal'
                ? t(`yourPersonalDiscountApplied`, 'Your personal discount is applied') + `: ${personalDiscount.type === 'percent' ? personalDiscount.value + '%' : personalDiscount.value.toFixed(2) + '€/' + t(`piece`, 'tk')} (Общая: ${generalDiscountType === 'percent' ? generalDiscountValue + '%' : generalDiscountValue.toFixed(2) + '€/' + t(`piece`, 'tk')})`
                : t(`generalDiscountApplied`, 'General discount is applied') + `: ${generalDiscountType === 'percent' ? generalDiscountValue + '%' : generalDiscountValue.toFixed(2) + t(`piece`, 'tk')} (Ваша персональная: ${personalDiscount.type === 'percent' ? personalDiscount.value + '%' : personalDiscount.value.toFixed(2) + '€/' + t(`piece`, 'tk')})`)
            : appliedDiscountType === 'personal'
              ? (personalDiscount.type === 'percent' ? t(`yourPersonalDiscount`, `Your peronal discount`) : t(`yourPersonalDiscountUnit`, 'Your personal discount unit'))
              : (generalDiscountType === 'percent' ? t('discount', 'Discount') : t('discount', 'Discount'))
        }>
          {discountDisplay}
        </div>
      )}
      
      <div className="product-image-container">
        <img 
          src={product.effectiveImageUrl || product.imageUrl || '/images/placeholder.jpg'} 
          alt={product.name}
          className="product-image"
          onError={(e) => {
            e.target.src = '/images/placeholder.jpg';
          }}
        />
      </div>

      <div className="product-info">
        <h3 className="product-name">{product.name}</h3>
        
        <div className="product-price-section">
          <div className="price-container">
            {hasDiscount && (
              <span className="original-price">{product.price.toFixed(2)} €</span>
            )}
            <span className="product-price">
              {price.toFixed(2)} € / {getUnitLabel(product.unit)}
            </span>
          </div>

          {/* Показываем информацию о кратности для кг товаров */}
          {product.unit === 'kg' && multiplicity > 1 && (
            <div className="multiplicity-info" style={{ fontSize: '0.85rem', color: '#666', marginTop: '4px' }}>
              {t('step', 'Step')}: {multiplicity} {t('kg')} = {(price * multiplicity).toFixed(2)} €
            </div>
          )}

          {product.sellByBox && (
            <div className="box-info">
              {t('box')}: {product.boxQuantity} {getUnitLabel(product.unit)} = {boxPrice?.toFixed(2)} €
            </div>
          )}
        </div>

        <div className="product-actions">
          {/* Покупка ящиками */}
          {product.sellByBox && product.boxPrice && (
            <div className="unit-section box-section">
              {cartQuantityBox > 0 ? (
                <div className="quantity-controls">
                  <button 
                    className="quantity-btn"
                    onClick={(e) => handleDecrement(e, 'box')}
                  >
                    <FaMinus />
                  </button>
                  <span className="quantity-display">{cartQuantityBox} {t('boxUnit')}</span>
                  <button 
                    className="quantity-btn"
                    onClick={(e) => handleIncrement(e, 'box')}
                  >
                    <FaPlus />
                  </button>
                </div>
              ) : (
                <>
                  <div className="quantity-controls">
                    <button 
                      className="quantity-btn"
                      onClick={(e) => handleDecrement(e, 'box')}
                    >
                      <FaMinus />
                    </button>
                    <span className="quantity-display">{quantityBox}</span>
                    <button 
                      className="quantity-btn"
                      onClick={(e) => handleIncrement(e, 'box')}
                    >
                      <FaPlus />
                    </button>
                  </div>
                  <button 
                    className="add-to-cart-btn btn-box"
                    onClick={(e) => handleAddToCart(e, 'box')}
                  >
                    <FaShoppingCart /> {t('addBox', 'Add box')}
                  </button>
                </>
              )}
            </div>
          )}

          {/* Покупка по штукам/кг */}
          <div className="unit-section">
            {cartQuantityPiece > 0 ? (
              <div className="quantity-controls">
                <button 
                  className="quantity-btn"
                  onClick={(e) => handleDecrement(e, 'piece')}
                >
                  <FaMinus />
                </button>
                <span 
                  className="quantity-display"
                  style={product.unit === 'kg' ? {
                    width: '65px',
                    display: 'inline-block',
                    textAlign: 'center'
                  } : {}}
                >
                  {cartQuantityPiece} {product.unit === 'kg' ? t('kg') : t('pieces')}
                </span>
                <button 
                  className="quantity-btn"
                  onClick={(e) => handleIncrement(e, 'piece')}
                >
                  <FaPlus />
                </button>
              </div>
            ) : (
              <>
                <div className="quantity-controls">
                  <button 
                    className="quantity-btn"
                    onClick={(e) => handleDecrement(e, 'piece')}
                  >
                    <FaMinus />
                  </button>
                  <span 
                    className="quantity-display"
                    style={product.unit === 'kg' ? {
                      width: '65px',
                      display: 'inline-block',
                      textAlign: 'center'
                    } : {}}
                  >
                    {quantityPiece} {product.unit === 'kg' ? t('kg') : ''}
                  </span>
                  <button 
                    className="quantity-btn"
                    onClick={(e) => handleIncrement(e, 'piece')}
                  >
                    <FaPlus />
                  </button>
                </div>
                <button 
                  className="add-to-cart-btn"
                  onClick={(e) => handleAddToCart(e, 'piece')}
                >
                  <FaShoppingCart /> {product.unit === 'kg' 
                    ? `${t('add', 'Add')} ${quantityPiece} ${t('kg')}`
                    : t('addProduct', 'Add product')}
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </Link>
  );
};

export default ProductCard;

